import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
    Video,
    MapPin,
    Search,
    Mic,
    MonitorPlay,
    FileAudio,
    Camera,
    PenLine,
    Info,
} from "lucide-react";

import { Patient, CouplePatient, groupLabel, type GroupPatient } from "../../../patient";
import { listTherapistPatients, listTherapistCouplePatients, listGroupPatients } from "../../application/sessionPatients";
import { sessionService } from "../../application/SessionApplicationService";
import SubjectKindIcon, { type SubjectKind } from "../../../../shared/ui/icons/SubjectKindIcon";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import { LoadingOverlay } from "../../../../shared/ui/common/LoadingOverlay";
import { useTherapist } from "../../../../context/useTherapist";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import Label from "../../../../shared/ui/form/Label";
import { TemplateSelector } from "../../../session_templates";
import { useCourseLabels, useSessionPreferences } from "../../../center";
import Button from "../../../../shared/ui/button/Button";

import { Modal } from "../../../../shared/ui/modal";
import { VideoCallRecorder } from "../../../../services/video-call-recorder";
import { useAlchelyMeetingAdopted } from "../../../marketplace";
import { useAiInsightsGate } from "../../../ai_insights";


interface QuickRecordModalProps {
    onClose: () => void;
}

/** The kinds of session, in the order the toggle offers them. */
const SUBJECT_KINDS: { kind: SubjectKind; labelKey: string }[] = [
    { kind: "individual", labelKey: "patient.individualTab" },
    { kind: "couple", labelKey: "patient.coupleSession" },
    { kind: "group", labelKey: "session.typeOptions.group" },
];

/** The six ways a session can be started, in the order the modal offers them. */
type SessionMode =
    | "alchely_meeting"
    | "video_call"
    | "live"
    | "upload_audio"
    | "image_notes"
    | "manual_notes";

/**
 * These start the moment the wizard opens, so their session is already in
 * flight; the rest only gather material the therapist supplies later.
 */
const RECORDING_MODES: SessionMode[] = [
    "live",
    "video_call",
    "alchely_meeting",
];

/** Field labels; tighter above `sm`, where the whole modal has to fit a laptop. */
const LABEL_CLASS =
    "mb-2 sm:mb-1.5 uppercase text-xs font-semibold text-content-tertiary tracking-wider";

export default function QuickRecordModal({ onClose }: QuickRecordModalProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { therapist, refreshTherapist } = useTherapist();
    const alchelyMeetingAdopted = useAlchelyMeetingAdopted();
    const { adopted: memoryAdopted } = useAiInsightsGate();
    // A centre may leave the clinical fields out of session creation.
    const { therapyFieldsEnabled } = useSessionPreferences();

    // `label` is the person alone, which names the session; a student's
    // course is added only where they are picked.
    const [patients, setPatients] = useState<{ label: string; value: string; courseId?: string | null }[]>([]);
    const { courseOf } = useCourseLabels();
    const patientOptions = useMemo(
        () =>
            patients.map((option) => {
                const course = courseOf(option.courseId);
                return course ? { ...option, label: `${option.label} · ${course}` } : option;
            }),
        [patients, courseOf]
    );
    const [selectedPatient, setSelectedPatient] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [sessionType, setSessionType] = useState<SubjectKind>("individual");
    // Only the kinds of session the centre runs; the first one left is the default.
    const { modes: sessionModes } = useSessionPreferences();
    const subjectKinds = SUBJECT_KINDS.filter(({ kind }) => sessionModes[kind]);
    useEffect(() => {
        if (subjectKinds.length > 0 && !subjectKinds.some(({ kind }) => kind === sessionType)) {
            setSessionType(subjectKinds[0].kind);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionType, sessionModes.individual, sessionModes.couple, sessionModes.group]);
    // Starts from what the therapist declared they practise, so the common
    // case needs no answer at all. Changing it here changes this session only.
    const [therapyTypes, setTherapyTypes] = useState<string[]>(
        therapist?.therapy_types ?? [],
    );
    const [ubication, setUbication] = useState("online");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Whatever was listed belonged to the previous kind: it goes at once,
        // and a slow answer for that kind is ignored when it lands — otherwise
        // a group's id could end up sent as a couple's.
        let stale = false;
        setPatients([]);
        setSelectedPatient("");
        const fetchPatients = async () => {
            if (!therapist?.id) return;
            try {
                let opts: { label: string; value: string; courseId?: string | null }[];
                if (sessionType === "individual") {
                    const data = await listTherapistPatients(therapist.id);
                    opts = data.patients.map((p: Patient) => ({
                        label: `${p.first_name} ${p.last_name}`,
                        value: p.id,
                        courseId: p.course_id,
                    }));
                } else if (sessionType === "group") {
                    const data = await listGroupPatients();
                    opts = data.map((g: GroupPatient) => ({ label: groupLabel(g), value: g.id }));
                } else {
                    const data = await listTherapistCouplePatients(therapist.id);
                    opts = data.map((c: CouplePatient) => ({
                        label: c.display_name,
                        value: c.id,
                    }));
                }
                if (!stale) setPatients(opts);
            } catch (e) {
                console.error("Error fetching patients", e);
            }
        };
        fetchPatients();
        return () => {
            stale = true;
        };
    }, [therapist, sessionType]);

    const handleStart = async (mode: SessionMode) => {
        if (!selectedPatient || loading) return;
        setLoading(true);
        setError(null);

        try {
            const patient = patients.find((p) => p.value === selectedPatient);
            const patientName = patient ? patient.label : "";
            const date = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
            const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            // Create session
            const created = await sessionService.createSession({
                date: date,
                title: `${date} - ${patientName}`,
                patient_id: sessionType === 'individual' ? selectedPatient : undefined,
                couple_patient_id: sessionType === 'couple' ? selectedPatient : undefined,
                group_patient_id: sessionType === 'group' ? selectedPatient : undefined,
                template_id: selectedTemplate || undefined,
                therapy_type: therapyFieldsEnabled ? therapyTypes.join(", ") : "",
                therapist_id: therapist?.id || "",
                hour: time,
                ubication: ubication,
                type: sessionType,
                duration: 50, // Default duration
                notes: "",
                // Only a recording mode is under way from the start; the others
                // still need the therapist to upload or write something.
                status: RECORDING_MODES.includes(mode) ? "processing" : undefined,
            });

            refreshTherapist();
            onClose();

            // The call gets its own tab; every other mode is a step of the
            // wizard, so it opens there.
            if (mode === "alchely_meeting") {
                window.open(
                    `/meet/session/${created.id}`,
                    "_blank",
                    "noopener"
                );
                return;
            }

            navigate("/sessions/new", {
                state: {
                    sessionId: created.id,
                    sessionType: mode,
                },
            });

        } catch (err) {
            console.error(err);
            setError(t("session.errorCreating"));
        } finally {
            setLoading(false);
        }
    };

    /**
     * A browser that cannot share tab audio still sees the option, greyed out
     * and with the reason spelled out — hiding it only reads as a missing
     * feature, and the therapist never learns another browser would do it.
     */
    const captureUnsupported = !VideoCallRecorder.isSupported();

    const modes: {
        type: SessionMode;
        label: string;
        icon: ReactNode;
        unavailableReason?: string;
    }[] = [
        // Absent rather than disabled: an unadopted tool is not a broken
        // option, it is one this therapist has not taken from the catalog.
        ...(alchelyMeetingAdopted
            ? [
                  {
                      type: "alchely_meeting" as const,
                      label: t("session.quickRecord.modes.alchely_meeting"),
                      icon: <Video className="w-5 h-5" />,
                  },
              ]
            : []),
        // Everything that hands Alchely raw material — a captured call, a
        // recording, a photo of your notes — exists so that something reads it.
        // Without Memoria aumentada nothing would, so none of it is offered.
        ...(memoryAdopted
            ? [
                  {
                      type: "video_call" as const,
                      label: t("session.quickRecord.modes.video_call"),
                      icon: <MonitorPlay className="w-5 h-5" />,
                      unavailableReason: captureUnsupported
                          ? t("session.videoCall.unsupported")
                          : undefined,
                  },
                  {
                      type: "live" as const,
                      label: t("session.quickRecord.modes.live"),
                      icon: <Mic className="w-5 h-5" />,
                  },
                  {
                      type: "upload_audio" as const,
                      label: t("session.quickRecord.modes.upload_audio"),
                      icon: <FileAudio className="w-5 h-5" />,
                  },
                  {
                      type: "image_notes" as const,
                      label: t("session.quickRecord.modes.image_notes"),
                      icon: <Camera className="w-5 h-5" />,
                  },
              ]
            : []),
        // Writing the session up yourself needs no tool at all, and is what
        // remains when none are adopted.
        {
            type: "manual_notes",
            label: t("session.quickRecord.modes.manual_notes"),
            icon: <PenLine className="w-5 h-5" />,
        },
    ];

    return (
        <Modal isOpen={true} onClose={onClose} showCloseButton={true} className="max-w-md overflow-hidden">
            {/* Body */}
            <div className="p-6 sm:p-5 space-y-6 sm:space-y-4">
                <div>
                    <h3 className="text-xl sm:text-lg font-bold text-content-primary mb-6 sm:mb-4">
                        {t("session.quickRecord.title")}
                    </h3>

                    <div className="space-y-5 sm:space-y-3.5">

                            {/* Session Type */}
                            <div>
                                <Label className={LABEL_CLASS} htmlFor="session-type">{t("session.sessionType")}</Label>
                                {/* One person, two, several: the icons say it at a glance,
                                    and the name stays as the button's label and tooltip. */}
                                <div className="flex bg-gray-100 dark:bg-white/5 p-1 rounded-xl" id="session-type" role="group">
                                    {subjectKinds.map(({ kind, labelKey }) => (
                                        <button
                                            key={kind}
                                            type="button"
                                            onClick={() => setSessionType(kind)}
                                            aria-pressed={sessionType === kind}
                                            aria-label={t(labelKey)}
                                            title={t(labelKey)}
                                            className={`flex-1 flex items-center justify-center px-4 py-2.5 sm:py-2 rounded-lg transition-all ${sessionType === kind ? 'bg-white dark:bg-white/10 text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                        >
                                            <SubjectKindIcon kind={kind} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Patient, couple or group */}
                            <div>
                                <Label className={LABEL_CLASS}>
                                    {sessionType === "group"
                                        ? t("patient.group.eyebrow")
                                        : sessionType === "couple"
                                          ? t("patient.coupleSession")
                                          : t("session.patient")}
                                </Label>
                                <FilterSelect
                                    options={patientOptions}
                                    value={selectedPatient}
                                    onChange={(val) => setSelectedPatient(val as string)}
                                    placeholder={
                                        sessionType === "individual"
                                            ? t("session.selectPatient")
                                            : t("patient.selectPatient")
                                    }
                                    searchPlaceholder={t("patient.searchPlaceholder")}
                                    className="w-full"
                                />
                            </div>

                            {/* Therapy Type */}
                            {therapyFieldsEnabled && (
                            <div>
                                <Label className={LABEL_CLASS}>{t("session.therapyType")} ({t("common.optional")})</Label>
                                <FilterSelect
                                    options={THERAPY_TYPES.map((type) => ({
                                        value: type,
                                        label: t(`session.therapyOptions.${type}`),
                                    }))}
                                    value={therapyTypes}
                                    onChange={(val) => {
                                        const arr = Array.isArray(val) ? val : [val];
                                        setTherapyTypes(arr);
                                    }}
                                    placeholder={t("session.therapyType")}
                                    searchPlaceholder={t("common.search")}
                                    icon={<Search className="w-5 h-5" />}
                                    multiple={true}
                                    allowCustomValues={true}
                                    addCustomLabel={t("common.add")}
                                />
                            </div>
                            )}

                            {/* Template Selector */}
                            {therapyFieldsEnabled && (
                            <div>
                                <Label className={LABEL_CLASS}>
                                    {t("session.template", "Template")}
                                </Label>
                                <TemplateSelector
                                    value={selectedTemplate}
                                    onChange={setSelectedTemplate}
                                    className="w-full"
                                />
                            </div>
                            )}

                            {/* Location */}
                            <div>
                                <Label className={LABEL_CLASS}>{t("session.location", "Location")}</Label>
                                <div className="flex bg-gray-100 dark:bg-white/5 p-1 rounded-xl">
                                    <button
                                        type="button"
                                        onClick={() => setUbication('online')}
                                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-all ${ubication === 'online' ? 'bg-white dark:bg-white/10 text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >
                                        <Video className="w-4 h-4" />
                                        <span>{t("session.locationOptions.online")}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUbication('in person')}
                                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-all ${ubication === 'in person' ? 'bg-white dark:bg-white/10 text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                    >
                                        <MapPin className="w-4 h-4" />
                                        <span>{t("session.locationOptions.in_person")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {error && <p className="mt-4 text-red-500 text-sm text-center bg-red-50 dark:bg-red-500/10 p-2 rounded-lg">{error}</p>}

                        {/* Mode picker — choosing one creates the session and
                            opens the wizard already on that mode's step. */}
                        <div className="pt-6 mt-6 sm:pt-4 sm:mt-4 border-t border-gray-100 dark:border-gray-800">
                            <Label className="mb-3 sm:mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider">
                                {t("session.quickRecord.chooseMode")}
                            </Label>
                            <div className="grid grid-cols-3 gap-2">
                                {modes.map((mode) => (
                                    <button
                                        key={mode.type}
                                        type="button"
                                        onClick={() => handleStart(mode.type)}
                                        disabled={
                                            !selectedPatient ||
                                            loading ||
                                            Boolean(mode.unavailableReason)
                                        }
                                        title={mode.unavailableReason}
                                        aria-describedby={
                                            mode.unavailableReason
                                                ? `${mode.type}-unavailable`
                                                : undefined
                                        }
                                        className="flex flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-2 py-3 sm:py-2.5 text-center transition-all hover:border-brand-300 hover:bg-brand-50/50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:bg-transparent dark:hover:bg-brand-500/10 dark:disabled:hover:border-gray-700"
                                    >
                                        <span className="text-brand-600">{mode.icon}</span>
                                        <span className="text-xs font-medium leading-tight text-gray-700 dark:text-gray-200">
                                            {mode.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {modes
                                .filter((mode) => mode.unavailableReason)
                                .map((mode) => (
                                    <p
                                        key={mode.type}
                                        id={`${mode.type}-unavailable`}
                                        className="mt-3 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500"
                                    >
                                        <Info
                                            className="w-3.5 h-3.5 shrink-0 mt-px"
                                            aria-hidden="true"
                                        />
                                        <span>{mode.unavailableReason}</span>
                                    </p>
                                ))}
                            {!selectedPatient && (
                                <p className="mt-3 text-xs text-center text-content-tertiary">
                                    {t("session.quickRecord.selectPatientFirst")}
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 sm:pt-4">
                            <Button variant="outline" onClick={onClose} disabled={loading}>
                                {t("common.cancel", "Cancel")}
                            </Button>
                        </div>
                    </div>
                </div>

                <LoadingOverlay visible={loading} />
        </Modal>

    );
}
